"use client";

import type { ReactNode } from "react";
import {
  ArrowRight,
  Button,
  ButtonLink,
  Check,
  FileSpreadsheet,
  Icon,
  MapPin,
  Share2,
} from "@/components";
import { MarketingCta } from "@/components/marketing-cta";
import "./landing.css";

/**
 * Landing sections — the modern-warm homepage (concept: concepts/modern-warm)
 * rebuilt as token-only presentational components. They compose AROUND the
 * real IntakeSurface in IngestEditor's no-data branch: the hero hosts the
 * working intake card; everything else is marketing shell. No parsing logic
 * lives here, and every brand-facing string arrives as a prop (getTheme).
 *
 * The map artwork is a lightweight static illustration drawn entirely from
 * map.* tokens — no tile fetches on the landing, so first paint stays fast;
 * the REAL map appears the moment "Make map" runs.
 */

/* The anchor the closing CTA (and footer) scroll back to. */
const INTAKE_ANCHOR = "make-a-map";
const STEPS_ANCHOR = "how-it-works";
const ASK_ANCHOR = "ask-your-map";

/* ------------------------------------------------------------- pins */

/** Classic teardrop pin, colored by map.marker tokens. */
function Pin({
  variant = "default",
  width = 34,
}: {
  variant?: "default" | "answer" | "inverse";
  width?: number;
}) {
  const height = Math.round((width / 34) * 44);
  const fill =
    variant === "answer"
      ? "var(--map-marker-pin-answer-fill)"
      : variant === "inverse"
        ? "var(--color-accent-on-accent)"
        : "var(--map-marker-pin-fill)";
  const rim =
    variant === "answer"
      ? "var(--map-marker-pin-answer-rim)"
      : variant === "inverse"
        ? "var(--color-accent-on-accent)"
        : "var(--map-marker-pin-rim)";
  const dot = variant === "inverse" ? "var(--color-accent-default)" : "var(--map-marker-pin-dot)";
  return (
    <svg width={width} height={height} viewBox="0 0 34 44" fill="none" aria-hidden focusable={false}>
      <path
        d="M17 42.5C17 42.5 3.5 24.8 3.5 15.2C3.5 7.7 9.5 1.8 17 1.8C24.5 1.8 30.5 7.7 30.5 15.2C30.5 24.8 17 42.5 17 42.5Z"
        fill={fill}
        stroke={rim}
        strokeWidth="1.6"
      />
      <circle cx="17" cy="15.2" r="5" fill={dot} />
    </svg>
  );
}

/* ------------------------------------------------------------- hero */

/** Static bay-side cartography, every color a map.* token. */
function HeroMapArt() {
  return (
    <svg
      className="landing-heromap__art"
      viewBox="0 0 520 560"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
      focusable={false}
    >
      <rect width="520" height="560" fill="var(--map-tile-land)" />
      {/* landcover + urban blocks */}
      <ellipse cx="120" cy="120" rx="150" ry="110" fill="var(--map-tile-landcover)" fillOpacity="0.5" />
      <ellipse cx="200" cy="500" rx="190" ry="120" fill="var(--map-tile-building)" fillOpacity="0.65" />
      <ellipse cx="90" cy="330" rx="120" ry="90" fill="var(--map-tile-building)" fillOpacity="0.5" />
      {/* the bay — sweeps in from the top right */}
      <path
        d="M330 0 C310 70 350 120 320 190 C290 260 330 320 400 350 C470 380 520 360 520 360 L520 0 Z"
        fill="var(--map-tile-water)"
      />
      <path
        d="M520 430 C470 420 430 450 420 500 C412 540 440 560 440 560 L520 560 Z"
        fill="var(--map-tile-water)"
      />
      {/* parks */}
      <path
        d="M60 200 C110 180 170 190 180 230 C190 270 140 300 90 290 C50 282 30 220 60 200 Z"
        fill="var(--map-tile-greenspace)"
        fillOpacity="0.85"
      />
      <ellipse cx="260" cy="420" rx="55" ry="34" fill="var(--map-tile-greenspace)" fillOpacity="0.85" />
      {/* streets */}
      <g stroke="var(--map-tile-road-fill)" strokeLinecap="round" fill="none">
        <path d="M0 260 C90 250 200 270 300 250" strokeWidth="4" />
        <path d="M40 0 C60 120 40 260 80 400 C100 470 140 520 180 560" strokeWidth="4" />
        <path d="M0 380 C110 370 240 400 360 380" strokeWidth="3" />
        <path d="M140 60 C160 160 150 280 190 380" strokeWidth="2" strokeOpacity="0.85" />
        <path d="M0 320 C80 316 170 330 250 318" strokeWidth="2" strokeOpacity="0.85" />
        <path d="M0 450 C90 444 200 470 320 450" strokeWidth="2" strokeOpacity="0.85" />
        <path d="M230 120 C250 220 230 330 260 440" strokeWidth="2" strokeOpacity="0.85" />
      </g>
      {/* motorway */}
      <path
        d="M0 120 C120 140 240 90 330 150 C390 190 400 260 380 330"
        stroke="var(--map-tile-motorway)"
        strokeWidth="5"
        strokeLinecap="round"
        fill="none"
      />
      {/* shoreline hairline */}
      <path
        d="M330 0 C310 70 350 120 320 190 C290 260 330 320 400 350"
        stroke="var(--map-tile-road-casing)"
        strokeWidth="1.5"
        fill="none"
      />
    </svg>
  );
}

const HERO_PINS: {
  left: string;
  top: string;
  width: number;
  delayMs: number;
  variant?: "default" | "answer";
  chip?: { name: string; meta: string };
}[] = [
  { left: "26%", top: "58%", width: 28, delayMs: 200 },
  { left: "60%", top: "76%", width: 28, delayMs: 360 },
  {
    left: "38%",
    top: "30%",
    width: 36,
    delayMs: 540,
    variant: "answer",
    chip: { name: "Harbor Light Coffee", meta: "412 Bay St · plotted" },
  },
];

export interface LandingHeroProps {
  /** The REAL IntakeSurface (variant="hero") — the hero hosts it, never mocks it. */
  intake: ReactNode;
}

/** Hero — the sunny gradient panel: the working intake (Take A) beside a
 *  tightened "postcard" map that tells the file → plotted-pins story. The
 *  floating pill-nav is rendered ABOVE the landing (sticky, full-page) by the
 *  editor page, not inside this clipped section. */
export function LandingHero({ intake }: LandingHeroProps) {
  return (
    <section className="landing-hero" id={INTAKE_ANCHOR} aria-labelledby="landing-hero-title">
      <div className="landing-wrap landing-hero__grid">
        <div className="landing-hero__copy">
          <h1 className="landing-hero__title" id="landing-hero-title">
            Put your spreadsheet on the map.
          </h1>
          <p className="landing-hero__sub">
            Drop a file, paste rows, or link a Google Sheet — every address becomes a pin on a
            beautiful, shareable map. No setup, no GIS degree.
          </p>
          <div className="landing-hero__intake">{intake}</div>
        </div>

        <figure className="landing-heromap-figure">
          <div className="landing-heromap" aria-hidden>
            <HeroMapArt />
            <span className="landing-heromap__file">
              <span className="landing-heromap__file-glyph">
                <Icon icon={FileSpreadsheet} size="sm" />
              </span>
              stores.csv · 3 rows
            </span>
            {HERO_PINS.map((pin) => (
              <span key={pin.left + pin.top} className="landing-pin" style={{ left: pin.left, top: pin.top }}>
                <span className="landing-pin__drop" style={{ animationDelay: `${pin.delayMs}ms` }}>
                  <Pin variant={pin.variant} width={pin.width} />
                  {pin.chip && (
                    <span className="landing-pin__chip">
                      <span className="landing-pin__name">{pin.chip.name}</span>
                      <span className="landing-pin__meta">{pin.chip.meta}</span>
                    </span>
                  )}
                </span>
              </span>
            ))}
            <span className="landing-heromap__pill">
              <span className="landing-heromap__pill-check">
                <Icon icon={Check} size="sm" />
              </span>
              3 addresses plotted
            </span>
          </div>
        </figure>
      </div>
    </section>
  );
}

/* ------------------------------------------------------- three steps */

const STEPS = [
  {
    n: "1",
    icon: FileSpreadsheet,
    title: "Drop your file",
    body: "CSV, Excel, or a paste straight from Sheets. We find the address columns for you.",
  },
  {
    n: "2",
    icon: MapPin,
    title: "Watch it plot",
    body: "Every row is geocoded and pinned in seconds, styled and ready to explore.",
  },
  {
    n: "3",
    icon: Share2,
    title: "Share the link",
    body: "Send it to anyone, embed it on your site, or keep it private to your team.",
  },
] as const;

/** "Three steps, no manual." — the dashed connector + staggered step tiles. */
export function LandingSteps() {
  return (
    <section className="landing-steps" id={STEPS_ANCHOR} aria-labelledby="landing-steps-title">
      <div className="landing-wrap">
        <header className="landing-steps__head">
          <h2 className="landing-h2" id="landing-steps-title">
            Three steps, no manual.
          </h2>
          <p className="landing-steps__sub">If you can attach a file to an email, you can make a map.</p>
        </header>

        <div className="landing-steps__board">
          <svg
            className="landing-steps__connector"
            viewBox="0 0 1000 130"
            preserveAspectRatio="none"
            aria-hidden
            focusable={false}
          >
            <path
              d="M 170 28 C 340 110, 500 4, 620 52 S 850 110, 860 74"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeDasharray="2 9"
              strokeLinecap="round"
              strokeOpacity="0.55"
            />
          </svg>
          <ol className="landing-steps__grid">
            {STEPS.map((step) => (
              <li key={step.n} className="landing-step">
                <span className="landing-step__tile">
                  <Icon icon={step.icon} size="xl" />
                  <span className="landing-step__num" aria-hidden>
                    {step.n}
                  </span>
                </span>
                <h3 className="landing-step__title">{step.title}</h3>
                <p className="landing-step__body">{step.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------ ask your map */

/** Quiet country field with the answer lit up — map.* tokens only. */
function AskMapArt() {
  return (
    <svg
      className="landing-askmap__art"
      viewBox="0 0 560 440"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
      focusable={false}
    >
      <rect width="560" height="440" fill="var(--map-tile-land)" />
      <ellipse cx="140" cy="90" rx="180" ry="90" fill="var(--map-tile-landcover)" fillOpacity="0.4" />
      <ellipse cx="430" cy="360" rx="160" ry="90" fill="var(--map-tile-landcover)" fillOpacity="0.4" />
      {/* coasts */}
      <path d="M0 320 C70 310 110 360 100 440 L0 440 Z" fill="var(--map-tile-water)" />
      <path d="M560 40 C520 70 490 40 500 0 L560 0 Z" fill="var(--map-tile-water)" />
      {/* state lines */}
      <g stroke="var(--map-tile-boundary)" strokeWidth="1.2" strokeDasharray="4 4" fill="none" strokeOpacity="0.6">
        <path d="M180 0 C190 120 170 300 200 440" />
        <path d="M370 0 C360 140 390 300 370 440" />
        <path d="M0 160 C140 180 300 150 560 190" />
        <path d="M0 300 C160 280 340 320 560 300" />
      </g>
      {/* the rest of the dataset, dimmed */}
      <g fill="var(--map-marker-dimmed-fill)" stroke="var(--map-marker-ring-color)" strokeWidth="1" fillOpacity="0.55">
        <circle cx="80" cy="110" r="4" />
        <circle cx="130" cy="240" r="4" />
        <circle cx="240" cy="80" r="4" />
        <circle cx="260" cy="330" r="4" />
        <circle cx="320" cy="200" r="4" />
        <circle cx="410" cy="90" r="4" />
        <circle cx="450" cy="260" r="4" />
        <circle cx="500" cy="150" r="4" />
        <circle cx="350" cy="390" r="4" />
        <circle cx="180" cy="380" r="4" />
      </g>
    </svg>
  );
}

const ASK_MATCHES = [
  { left: "30%", top: "42%", label: "Bayline Storage" },
  { left: "56%", top: "64%", label: "Redrock Depot" },
  { left: "76%", top: "34%", label: "Northgate Yard" },
];

const ASK_CHIPS = ["clinics near the coasts", "top metro by revenue", "depots under $300k"];

/** "Maps that answer back." — the ask-your-map promise, shown not told. */
export function LandingAsk() {
  return (
    <section className="landing-ask" id={ASK_ANCHOR} aria-labelledby="landing-ask-title">
      <div className="landing-wrap">
        <div className="landing-ask__panel">
          <div>
            <span className="landing-ask__badge">New · Ask your map</span>
            <h2 className="landing-h2 landing-ask__title" id="landing-ask-title">
              Maps that answer back.
            </h2>
            <p className="landing-ask__sub">
              Ask a question the way you’d say it out loud. The map lights up the answer and tells
              you what it found.
            </p>
            <div className="landing-ask__prompt">
              <svg className="landing-ask__spark" width="18" height="18" viewBox="0 0 20 20" aria-hidden focusable={false}>
                <path d="M10 1l1.9 6.1L18 9l-6.1 1.9L10 17l-1.9-6.1L2 9l6.1-1.9L10 1z" fill="currentColor" />
              </svg>
              <span className="landing-ask__q">Show me warehouses over $400k</span>
              <span className="landing-ask__go" aria-hidden>
                <Icon icon={ArrowRight} size="md" />
              </span>
            </div>
            <div className="landing-ask__chips">
              {ASK_CHIPS.map((chip) => (
                <span key={chip} className="landing-ask__chip">
                  {chip}
                </span>
              ))}
            </div>
          </div>

          <div className="landing-askmap" aria-hidden>
            <AskMapArt />
            {ASK_MATCHES.map((match) => (
              <span key={match.label} className="landing-askmap__match" style={{ left: match.left, top: match.top }}>
                <span className="landing-askmap__dot">
                  <span className="landing-askmap__pulse" />
                </span>
                <span className="landing-askmap__label">{match.label}</span>
              </span>
            ))}
            <p className="landing-askmap__answer">
              <strong>Found 3</strong>, together worth $1.9M. The biggest is Bayline Storage.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------- live sync */

const SYNC_ANCHOR = "live-sync";

/** Live sync — "Your map keeps up with your data." The source → refresh → map
 *  story (a Google Sheet card, a refresh pill on the seam, a mini map that's
 *  already current) plus the honest connector list (Sheets/CSV/Excel live; the
 *  CRM trio tagged "Coming soon" — the OAuth handshake isn't wired yet). */
export function LandingSync({ brandName, onChooseFile }: { brandName: string; onChooseFile: () => void }) {
  return (
    <section className="landing-sync" id={SYNC_ANCHOR} aria-labelledby="landing-sync-title">
      <div className="landing-wrap landing-sync__grid">
        <div className="landing-sync__copy">
          <span className="landing-sync__badge">Live sync</span>
          <h2 className="landing-h2 landing-sync__title" id="landing-sync-title">
            Your map keeps up with your data.
          </h2>
          <p className="landing-sync__sub">
            Connect a spreadsheet or a Google Sheet once. {brandName} re-reads the source and moves
            the pins for you — every day on a schedule, or the moment you ask. No re-uploading, no
            rebuilding.
          </p>
          <ul className="landing-sync__points">
            <li className="landing-sync__point">
              <span className="landing-sync__point-icon" aria-hidden>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" focusable={false}>
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
              </span>
              <span className="landing-sync__point-body">
                <strong className="landing-sync__point-label">Connect once</strong>
                Link a Google Sheet or bring a spreadsheet as a living source. Change the source; the
                map catches up.
              </span>
            </li>
            <li className="landing-sync__point">
              <span className="landing-sync__point-icon" aria-hidden>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" focusable={false}>
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" />
                </svg>
              </span>
              <span className="landing-sync__point-body">
                <strong className="landing-sync__point-label">Refresh on your terms</strong>
                A fresh read every day on a schedule, or on demand with a click.
              </span>
            </li>
            <li className="landing-sync__point">
              <span className="landing-sync__point-icon" aria-hidden>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" focusable={false}>
                  <rect x="5" y="11" width="14" height="9" rx="2" />
                  <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                </svg>
              </span>
              <span className="landing-sync__point-body">
                <strong className="landing-sync__point-label">Credentials stay encrypted</strong>
                Connection details are encrypted at rest. Your data stays yours.
              </span>
            </li>
          </ul>
          <Button
            type="button"
            onClick={onChooseFile}
            variant="primary"
            size="lg"
            className="landing-sync__cta"
            iconTrailing={
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable={false}>
                <path d="M5 12h14" />
                <path d="m12 5 7 7-7 7" />
              </svg>
            }
          >
            Connect a sheet
          </Button>
        </div>

        <div className="landing-sync__viz">
          <div className="landing-sync__stage" aria-hidden>
            <svg className="landing-sync__flow" viewBox="0 0 480 400" preserveAspectRatio="none" focusable={false}>
              <path className="landing-sync__flow-path" d="M 338 84 C 452 116, 466 234, 380 272" fill="none" strokeWidth="2" strokeLinecap="round" />
            </svg>

            <div className="landing-sync__source">
              <div className="landing-sync__source-head">
                <span className="landing-sync__sheet-glyph">
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" focusable={false}>
                    <rect x="3" y="2.5" width="14" height="15" rx="1.8" />
                    <path d="M3 7.5h14" />
                    <path d="M9.5 7.5v10" />
                    <path d="M3 12.5h14" />
                  </svg>
                </span>
                <span className="landing-sync__source-title">Google Sheet · Store locations</span>
              </div>
              <div className="landing-sync__rows">
                <span className="landing-sync__row"><i /><i /></span>
                <span className="landing-sync__row"><i /><i /></span>
                <span className="landing-sync__row"><i /><i /></span>
                <span className="landing-sync__row landing-sync__row--new"><i /><i /></span>
              </div>
            </div>

            <div className="landing-sync__refresh">
              <span className="landing-sync__refresh-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" focusable={false}>
                  <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                  <path d="M21 3v5h-5" />
                  <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                  <path d="M8 16H3v5" />
                </svg>
              </span>
              Refreshed today, 9:00 AM · next run tomorrow
            </div>

            <div className="landing-sync__map">
              <svg className="landing-sync__map-art" viewBox="0 0 460 240" preserveAspectRatio="xMidYMid slice" focusable={false}>
                <rect width="460" height="240" fill="var(--map-tile-land)" />
                <ellipse cx="380" cy="44" rx="140" ry="72" fill="var(--map-tile-landcover)" fillOpacity="0.45" />
                <ellipse cx="70" cy="204" rx="130" ry="66" fill="var(--map-tile-building)" fillOpacity="0.6" />
                <path d="M0 150 C60 132 92 172 82 240 L0 240 Z" fill="var(--map-tile-water)" />
                <path d="M296 28 C348 12 410 30 416 62 C422 92 380 112 336 104 C298 96 272 42 296 28 Z" fill="var(--map-tile-greenspace)" fillOpacity="0.85" />
                <g stroke="var(--map-tile-road-fill)" strokeLinecap="round" fill="none">
                  <path d="M0 92 C110 82 240 102 460 84" strokeWidth="4" />
                  <path d="M150 0 C160 80 148 160 170 240" strokeWidth="3" />
                  <path d="M0 182 C120 172 260 192 460 176" strokeWidth="2" strokeOpacity="0.85" />
                  <path d="M300 0 C312 70 300 150 322 240" strokeWidth="2" strokeOpacity="0.85" />
                </g>
                <path d="M0 40 C120 62 300 18 460 44" stroke="var(--map-tile-motorway)" strokeWidth="5" strokeLinecap="round" fill="none" />
              </svg>
              <span className="landing-sync__dot" style={{ left: "24%", top: "32%" }} />
              <span className="landing-sync__dot" style={{ left: "44%", top: "70%" }} />
              <span className="landing-sync__dot" style={{ left: "79%", top: "42%" }} />
              <span className="landing-sync__pin" style={{ left: "61%", top: "56%" }}>
                <svg className="landing-sync__pin-art" width="26" height="34" viewBox="0 0 34 44" fill="none" focusable={false}>
                  <path
                    d="M17 42.5C17 42.5 3.5 24.8 3.5 15.2C3.5 7.7 9.5 1.8 17 1.8C24.5 1.8 30.5 7.7 30.5 15.2C30.5 24.8 17 42.5 17 42.5Z"
                    fill="var(--map-marker-pin-answer-fill)"
                    stroke="var(--map-marker-pin-answer-rim)"
                    strokeWidth="1.6"
                  />
                  <circle cx="17" cy="15.2" r="5" fill="var(--map-marker-pin-dot)" />
                </svg>
                <span className="landing-sync__pin-ping" />
              </span>
              <span className="landing-sync__map-pill">
                <span className="landing-sync__map-pill-check">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" focusable={false}>
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </span>
                Up to date
              </span>
            </div>
          </div>

          <ul className="landing-sync__connectors">
            <li className="landing-sync__conn">Google Sheets</li>
            <li className="landing-sync__conn">CSV</li>
            <li className="landing-sync__conn">Excel</li>
            <li className="landing-sync__conn landing-sync__conn--soon">
              Salesforce
              <span className="landing-sync__conn-soon">Coming soon</span>
            </li>
            <li className="landing-sync__conn landing-sync__conn--soon">
              HubSpot
              <span className="landing-sync__conn-soon">Coming soon</span>
            </li>
            <li className="landing-sync__conn landing-sync__conn--soon">
              Zoho
              <span className="landing-sync__conn-soon">Coming soon</span>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------- public API */

const API_ANCHOR = "developers";

/** Public API — "Maps your code can drive." A dark on-brand code card showing a
 *  real /v1 curl + a 200 response chip. Every claim traces to the shipped
 *  contract; API keys are a paid capability (the note says so), and export/embed
 *  live only in the quiet "also" line (export is a download, not an endpoint). */
export function LandingApi({ apiBaseUrl }: { apiBaseUrl: string }) {
  return (
    <section className="landing-api" id={API_ANCHOR} aria-labelledby="landing-api-title">
      <div className="landing-wrap">
        <div className="landing-api__panel">
          <div className="landing-api__copy">
            <span className="landing-api__badge">Developers</span>
            <h2 className="landing-h2 landing-api__title" id="landing-api-title">
              Maps your code can drive.
            </h2>
            <p className="landing-api__sub">
              A versioned REST API over your datasets and maps. Push rows, trigger a refresh, publish
              a share — from your own code, with a single bearer key.
            </p>
            <ul className="landing-api__points">
              <li className="landing-api__point">
                <span className="landing-api__point-icon" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" focusable={false}>
                    <path d="M8 3H7a2 2 0 0 0-2 2v5a2 2 0 0 1-2 2 2 2 0 0 1 2 2v5a2 2 0 0 0 2 2h1" />
                    <path d="M16 21h1a2 2 0 0 0 2-2v-5a2 2 0 0 1 2-2 2 2 0 0 1-2-2V5a2 2 0 0 0-2-2h-1" />
                  </svg>
                </span>
                <span className="landing-api__point-body">
                  <strong className="landing-api__point-label">Clean, versioned responses</strong>
                  Canonical JSON with a <code className="landing-api__inline-code">schema_version</code> on
                  every response. No vendor payloads to untangle.
                </span>
              </li>
              <li className="landing-api__point">
                <span className="landing-api__point-icon" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" focusable={false}>
                    <circle cx="7.5" cy="15.5" r="4.5" />
                    <path d="M10.8 12.2 21 2" />
                    <path d="m15.5 7.5 3 3" />
                  </svg>
                </span>
                <span className="landing-api__point-body">
                  <strong className="landing-api__point-label">Keys you control</strong>
                  Read and write scopes, rotate or revoke anytime; writes are idempotent.
                </span>
              </li>
              <li className="landing-api__point">
                <span className="landing-api__point-icon" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" focusable={false}>
                    <path d="M12 12c-2-2.7-4-4-6-4a4 4 0 1 0 0 8c2 0 4-1.3 6-4Zm0 0c2 2.7 4 4 6 4a4 4 0 0 0 0-8c-2 0-4 1.3-6 4Z" />
                  </svg>
                </span>
                <span className="landing-api__point-body">
                  <strong className="landing-api__point-label">Never billed by the call</strong>
                  Your plan governs what you can do, not how many calls you make.
                </span>
              </li>
            </ul>
            <p className="landing-api__also">
              Plus embeddable maps (iframe, with a store-locator mode) and PNG, KML, GeoJSON, and PDF
              export.
            </p>
            <div className="landing-api__cta-row">
              <ButtonLink
                href="/help/developer-api/"
                variant="secondary"
                size="lg"
                iconTrailing={
                  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable={false}>
                    <path d="M5 12h14" />
                    <path d="m12 5 7 7-7 7" />
                  </svg>
                }
              >
                Read the API docs
              </ButtonLink>
              <span className="landing-api__note">API access is on paid plans.</span>
            </div>
          </div>

          <div className="landing-api__viz">
            <div className="landing-api__code" aria-hidden>
              <div className="landing-api__code-bar">
                <span className="landing-api__code-dot landing-api__code-dot--a" />
                <span className="landing-api__code-dot landing-api__code-dot--b" />
                <span className="landing-api__code-dot landing-api__code-dot--c" />
              </div>
              <pre className="landing-api__pre"><code>
                <span className="landing-api__t-cmd">curl</span>{" "}
                <span className="landing-api__t-flag">-X</span>{" "}
                <span className="landing-api__t-cmd">POST</span>{" "}
                <span className="landing-api__t-url">{apiBaseUrl}/datasets/ds_812/rows</span>{" "}
                <span className="landing-api__t-dim">\</span>{"\n  "}
                <span className="landing-api__t-flag">-H</span>{" "}
                <span className="landing-api__t-str">{'"Authorization: Bearer sk_live_9f2c…"'}</span>{" "}
                <span className="landing-api__t-dim">\</span>{"\n  "}
                <span className="landing-api__t-flag">-H</span>{" "}
                <span className="landing-api__t-str">{'"Idempotency-Key: a41e…"'}</span>{" "}
                <span className="landing-api__t-dim">\</span>{"\n  "}
                <span className="landing-api__t-flag">-d</span>{" "}
                <span className="landing-api__t-str">{'\'{"rows":[{"name":"Harbor Light","address":"412 Bay St"}]}\''}</span>
                <span className="landing-api__caret" />
              </code></pre>
              <div className="landing-api__result">
                <span className="landing-api__result-chip">
                  <strong>200</strong>{' · { "schema_version": "2025-06", "rows_added": 1 }'}
                </span>
                <span className="landing-api__result-note">The map refreshes on the next read.</span>
              </div>
            </div>
            <ul className="landing-api__tags">
              <li className="landing-api__tag">API keys</li>
              <li className="landing-api__tag">Idempotent writes</li>
              <li className="landing-api__tag">Embeddable maps</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------------------------- closing moment */

/** The closing CTA — the shared MarketingCta (same component the pricing page
 *  uses); its button scrolls to the hero intake anchor. */
export function LandingCta() {
  return (
    <MarketingCta
      title="Your data already knows where it belongs."
      lede="Drop in the spreadsheet. Watch the pins land."
      ctaLabel="Make my free map →"
      ctaHref={`#${INTAKE_ANCHOR}`}
      note="Free while in beta · No card required · Your data stays yours"
    />
  );
}

// The footer used to live here (LandingFooter). It's now the ONE global
// footer for every page — moved to `@/components/site-footer` (SiteFooter)
// per Justin's call to stop the landing page and every other route from
// showing two different-looking footers. Import it from there.
